/**
 * Outstanding Collections dashboard (Feb 2026, P1).
 *
 * Surfaces every pending / deferred payment-schedule stage across visible
 * Won leads so managers can chase collections without opening each lead.
 * Stage-level rows because a single lead can have overdue advance AND
 * upcoming delivery — the follow-up list needs both lines.
 *
 * Uses the same visibility model as Leads (backend applies
 * `build_lead_visibility_filter`), so SEs see only their own, managers see
 * their team, President/VP see everything.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { EmptyState } from '../components/EmptyState';
import {
  MagnifyingGlass, Wallet, WarningCircle, Clock, HourglassMedium,
  ArrowRight, Funnel, UserCircle, Warning, X, CaretDown,
} from '@phosphor-icons/react';
import { ViewToggle, useViewMode } from '../components/ViewToggle';
import { useTenantPath } from '../lib/tenantPath';
import useAuthStore from '../store/authStore';

import { notify } from '../lib/notify';
import { InlineSkeleton, TableRowsSkeleton } from '../components/Skeletons';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
  if (!iso) return '—';
  const p = String(iso).split('T')[0].split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso;
};

const BUCKET_CHIPS = [
  { key: '',        label: 'All' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_soon', label: 'Due Soon' },
  { key: 'deferred', label: 'Deferred' },
  { key: '0-30',    label: '0-30 d' },
  { key: '31-60',   label: '31-60 d' },
  { key: '61-90',   label: '61-90 d' },
  { key: '90+',     label: '90+ d' },
];

const agingBadge = (bucket, daysOverdue) => {
  if (daysOverdue === null || daysOverdue === undefined) {
    return <span className="text-[11px] text-slate-500">—</span>;
  }
  if (daysOverdue <= 0) {
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-emerald-50 text-emerald-700 border border-emerald-200">Due in {Math.abs(daysOverdue)}d</span>;
  }
  const color = bucket === '90+' ? 'bg-rose-100 text-rose-800 border-rose-300'
              : bucket === '61-90' ? 'bg-orange-100 text-orange-800 border-orange-300'
              : bucket === '31-60' ? 'bg-amber-100 text-amber-800 border-amber-300'
              : 'bg-yellow-50 text-yellow-800 border-yellow-200';
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${color} font-semibold`}>{daysOverdue}d overdue</span>;
};

const stageStatusPill = (status) => {
  if (status === 'deferred') return <span className="px-2 py-0.5 rounded-full text-[11px] bg-purple-100 text-purple-700">Deferred</span>;
  return <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-600">Pending</span>;
};

export default function Collections() {
  const tp = useTenantPath();
  // iter130 — URL-synced filters so a manager can share/bookmark a
  // "collections for Rakesh" view. `searchParams` is the single source of
  // truth; local state stays in sync via the effect below.
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bucket, setBucket] = useState(searchParams.get('bucket') || '');
  const [viewMode, setViewMode] = useViewMode('collections', 'list');
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('q') || '');
  const [daysAhead, setDaysAhead] = useState(parseInt(searchParams.get('days'), 10) || 30);
  const [ownerId, setOwnerId] = useState(searchParams.get('owner') || '');
  const [owners, setOwners] = useState([]);   // [{id, name, role}]
  // iter132.8 — inline reassign picker on "Owner missing" pill.
  const { user } = useAuthStore();
  const canReassign = ['National Sales Manager', 'Vice President', 'President'].includes(user?.role);
  const [assignables, setAssignables] = useState([]); // [{id, name, region, role}]
  const [reassignLeadId, setReassignLeadId] = useState(null); // which row's popover is open
  const [reassignBusy, setReassignBusy] = useState(false);
  useEffect(() => {
    if (!canReassign) return;
    axios.get(`${API}/users/sales-executives`)
      .then((r) => setAssignables(r.data?.users || []))
      .catch(() => setAssignables([]));
  }, [canReassign]);
  const handleReassign = async (leadId, newAssigneeId) => {
    setReassignBusy(true);
    try {
      await axios.post(`${API}/leads/${leadId}/reassign`, {
        new_assignee_id: newAssigneeId,
        force_cross_region: true,       // ownerless leads have no lead-region binding anyway
        reason: 'Reassigned from Collections (was unassigned)',
      });
      setReassignLeadId(null);
      // Re-fetch collections so the row's owner_name updates in place.
      await reload();
    } catch (e) {
      notify(e.response?.data?.detail || 'Failed to reassign lead');
    } finally {
      setReassignBusy(false);
    }
  };

  // One-shot: fetch the owners this user is allowed to filter by. Empty
  // response = SE (they only see their own anyway) → we hide the dropdown.
  useEffect(() => {
    axios.get(`${API}/team/searchable-owners`)
      .then((r) => setOwners(Array.isArray(r.data) ? r.data : (r.data.owners || [])))
      .catch(() => setOwners([]));
  }, []);

  // Push non-default filters onto the URL so the view is shareable.
  useEffect(() => {
    const next = new URLSearchParams();
    if (bucket) next.set('bucket', bucket);
    if (debouncedSearch) next.set('q', debouncedSearch);
    if (ownerId) next.set('owner', ownerId);
    if (daysAhead !== 30) next.set('days', String(daysAhead));
    setSearchParams(next, { replace: true });
  }, [bucket, debouncedSearch, ownerId, daysAhead, setSearchParams]);

  // Debounce search so we don't hammer the endpoint on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // iter132.8 — hoisted `load` so the inline-reassign popover can refresh
  // the row without waiting for a filter change.
  const reload = async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      if (bucket) params.set('bucket', bucket);
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (ownerId) params.set('owner_ids', ownerId);
      params.set('days_ahead', String(daysAhead));
      const r = await axios.get(`${API}/collections/outstanding?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load collections');
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, debouncedSearch, ownerId, daysAhead]);

  const summary = data?.summary || {
    total_outstanding: 0, lead_count: 0,
    overdue_amount: 0, overdue_count: 0,
    due_soon_amount: 0, due_soon_count: 0,
    deferred_amount: 0, deferred_count: 0,
    aging: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 },
  };
  const rows = data?.rows || [];

  const totalByOwner = useMemo(() => {
    const acc = {};
    rows.forEach(r => {
      const k = r.owner_name || 'Unassigned';
      acc[k] = (acc[k] || 0) + r.stage_balance;
    });
    return Object.entries(acc).sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="collections-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }}>
            Outstanding Collections
          </h1>
          <p className="text-sm text-[#6D8E87] mt-1">
            Overdue and upcoming payment stages across won orders — chase before quarter-end.
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <label className="text-[#6D8E87]">Due-soon window</label>
          <select
            value={daysAhead}
            onChange={e => setDaysAhead(Number(e.target.value))}
            className="border border-[#D5E8E4] rounded px-2 py-1 text-sm bg-white"
            data-testid="collections-days-ahead"
          >
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
            <option value={60}>60 days</option>
          </select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="collections-kpis">
        <KpiCard
          testId="kpi-total-outstanding"
          icon={<Wallet weight="duotone" className="text-[#00a69c]" size={22} />}
          label="Total Outstanding"
          value={fmtINR(summary.total_outstanding)}
          sub={`${summary.lead_count} lead${summary.lead_count === 1 ? '' : 's'}`}
          border="border-l-[#00a69c]"
        />
        <KpiCard
          testId="kpi-overdue"
          icon={<WarningCircle weight="duotone" className="text-rose-600" size={22} />}
          label="Overdue"
          value={fmtINR(summary.overdue_amount)}
          sub={`${summary.overdue_count} stage${summary.overdue_count === 1 ? '' : 's'}`}
          border="border-l-rose-500"
        />
        <KpiCard
          testId="kpi-due-soon"
          icon={<Clock weight="duotone" className="text-amber-600" size={22} />}
          label={`Due in ${daysAhead} d`}
          value={fmtINR(summary.due_soon_amount)}
          sub={`${summary.due_soon_count} stage${summary.due_soon_count === 1 ? '' : 's'}`}
          border="border-l-amber-500"
        />
        <KpiCard
          testId="kpi-deferred"
          icon={<HourglassMedium weight="duotone" className="text-purple-600" size={22} />}
          label="Deferred"
          value={fmtINR(summary.deferred_amount)}
          sub={`${summary.deferred_count} stage${summary.deferred_count === 1 ? '' : 's'}`}
          border="border-l-purple-500"
        />
      </div>

      {/* Aging strip */}
      <div className="medical-card p-4" data-testid="collections-aging-strip">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#3B5A54]">Aging (overdue only)</h3>
          <span className="text-xs text-[#6D8E87]">Days past due</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {['0-30', '31-60', '61-90', '90+'].map(b => (
            <button
              key={b}
              onClick={() => setBucket(bucket === b ? '' : b)}
              data-testid={`aging-bucket-${b}`}
              className={`text-left border rounded-lg px-3 py-3 transition ${
                bucket === b ? 'border-[#00a69c] bg-[#F4F9F7]' : 'border-[#D5E8E4] bg-white hover:border-[#00a69c]'
              }`}
            >
              <div className="text-[11px] uppercase tracking-wide text-[#6D8E87]">{b} days</div>
              <div className="text-lg font-bold text-[#00594F] mt-1">{fmtINR(summary.aging[b])}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Filters row */}
      <div className="flex flex-wrap items-center gap-2" data-testid="collections-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <Funnel size={16} className="text-[#6D8E87]" />
          {BUCKET_CHIPS.map(c => (
            <button
              key={c.key || 'all'}
              onClick={() => setBucket(c.key)}
              data-testid={`bucket-chip-${c.key || 'all'}`}
              className={`px-3 py-1 text-xs rounded-full border transition ${
                bucket === c.key
                  ? 'bg-[#00a69c] text-white border-[#00a69c]'
                  : 'bg-white text-[#3B5A54] border-[#D5E8E4] hover:border-[#00a69c]'
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        {/* iter130 — Owner dropdown. Hidden for SEs (owners == [] means the
            backend already restricted their scope, no filter needed). */}
        {owners.length > 1 && (
          <div className="relative">
            <UserCircle size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#6D8E87] pointer-events-none" />
            <select
              value={ownerId}
              onChange={(e) => setOwnerId(e.target.value)}
              className="pl-8 pr-8 py-2 border border-[#D5E8E4] rounded-lg text-sm bg-white focus:outline-none focus:border-[#00a69c] appearance-none"
              data-testid="collections-owner-filter"
              title="Filter by sales executive"
            >
              <option value="">All owners</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}{o.role ? ` · ${o.role}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="relative ml-auto">
          <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D8E87]" size={16} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search lead, customer or owner…"
            className="pl-9 pr-3 py-2 border border-[#D5E8E4] rounded-lg text-sm w-72 bg-white focus:outline-none focus:border-[#00a69c]"
            data-testid="collections-search"
          />
        </div>
      </div>

      {/* View toggle — Feb 2026 */}
      <div className="flex justify-end mb-2">
        <ViewToggle mode={viewMode} onChange={setViewMode} testIdPrefix="collections-view" />
      </div>

      {/* Table */}
      {viewMode === 'list' && (
      <div className="medical-card overflow-hidden">
        {error && (
          <div className="p-4 text-sm text-rose-700 bg-rose-50 border-b border-rose-200" data-testid="collections-error">
            {error}
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#F4F9F7]">
              <tr className="text-left text-[11px] uppercase text-[#3B5A54] tracking-wide">
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3">Customer</th>
                <th className="px-4 py-3">Owner</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">Paid</th>
                <th className="px-4 py-3 text-right">Balance</th>
                <th className="px-4 py-3">Due</th>
                <th className="px-4 py-3">Aging</th>
                <th className="px-4 py-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody data-testid="collections-tbody">
              {loading && (
                <TableRowsSkeleton cols={10} />
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={10}><EmptyState
                  title={`No outstanding payments${bucket ? ` in "${(BUCKET_CHIPS.find((b) => b.key === bucket) || {}).label || bucket}"` : ''}`}
                  hint={bucket || search ? 'Try another bucket or clear the search.' : 'Nice — everything is collected!'}
                  testid="collections-empty" /></td></tr>
              )}
              {!loading && rows.map((r, i) => (
                <tr key={`${r.lead_id}-${r.stage_id || i}`} className="border-t border-[#D5E8E4] hover:bg-[#F9FCFB]" data-testid={`collections-row-${i}`}>
                  <td className="px-4 py-3 text-sm font-medium text-[#00594F]">{r.lead_name || '—'}</td>
                  <td className="px-4 py-3 text-sm text-[#3B5A54]">
                    {r.customer_name || '—'}
                    {r.customer_code && <div className="text-[11px] text-[#6D8E87]">{r.customer_code}</div>}
                  </td>
                  <td className="px-4 py-3 text-sm text-[#3B5A54] relative">
                    {(r.owner_name || '').trim() ? (
                      <div>{r.owner_name}</div>
                    ) : canReassign ? (
                      <button
                        type="button"
                        onClick={() => setReassignLeadId(reassignLeadId === r.lead_id ? null : r.lead_id)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 hover:bg-amber-200 transition"
                        title="This lead has no assigned owner — click to reassign inline"
                        data-testid={`collections-owner-missing-${r.lead_id}`}
                      >
                        <Warning size={12} weight="bold" /> Owner missing <CaretDown size={10} weight="bold" />
                      </button>
                    ) : (
                      <Link
                        to={tp(`/leads/${r.lead_id}`)}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-800 hover:bg-amber-200 transition"
                        title="This lead has no assigned owner"
                        data-testid={`collections-owner-missing-${r.lead_id}`}
                      >
                        <Warning size={12} weight="bold" /> Owner missing
                      </Link>
                    )}
                    {r.region && <div className="text-[11px] text-[#6D8E87]">{r.region}</div>}
                    {reassignLeadId === r.lead_id && canReassign && (
                      <ReassignPopover
                        users={assignables}
                        busy={reassignBusy}
                        onSelect={(uid) => handleReassign(r.lead_id, uid)}
                        onClose={() => setReassignLeadId(null)}
                      />
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="text-[#3B5A54]">{r.stage_label}</div>
                    <div className="mt-1">{stageStatusPill(r.stage_status)}</div>
                    {r.deferral_count > 0 && (
                      <div className="text-[11px] text-purple-700 mt-1" title={r.last_deferral_reason}>
                        Deferred × {r.deferral_count}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-right tabular-nums">{fmtINR(r.stage_amount)}</td>
                  <td className="px-4 py-3 text-sm text-right tabular-nums text-emerald-700">{fmtINR(r.stage_paid)}</td>
                  <td className="px-4 py-3 text-sm text-right tabular-nums font-semibold text-[#00594F]">{fmtINR(r.stage_balance)}</td>
                  <td className="px-4 py-3 text-sm text-[#3B5A54]">{fmtDate(r.due_date)}</td>
                  <td className="px-4 py-3">{agingBadge(r.aging_bucket, r.days_overdue)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={tp(`/leads/${r.lead_id}`)}
                      className="inline-flex items-center gap-1 text-[13px] text-[#00a69c] hover:underline font-medium"
                      data-testid={`follow-up-link-${i}`}
                    >
                      Follow up <ArrowRight size={12} />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {viewMode === 'cards' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="collections-grid">
          {loading && <InlineSkeleton />}
          {!loading && rows.length === 0 && (
            <div className="col-span-full medical-card"><EmptyState compact title="No outstanding balances match this filter" /></div>
          )}
          {!loading && rows.map((r) => (
            <Link
              key={`${r.lead_id}-${r.stage_id}`}
              to={`/leads/${r.lead_id}`}
              className="medical-card p-4 hover:shadow-lg transition block"
              data-testid={`collections-card-${r.lead_id}-${r.stage_id}`}
            >
              <div className="flex items-start justify-between mb-1">
                <span className="text-[10px] font-bold uppercase tracking-wide text-[#6D8E87]">{r.stage_label}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  (r.days_overdue || 0) > 30 ? 'bg-rose-100 text-rose-700'
                  : (r.days_overdue || 0) > 0 ? 'bg-amber-100 text-amber-700'
                  : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {(r.days_overdue || 0) > 0 ? `${r.days_overdue}d overdue` : 'Due soon'}
                </span>
              </div>
              <h3 className="font-semibold text-[#052A23] text-base leading-tight">{r.lead_name}</h3>
              <p className="text-xs text-[#6D8E87] mb-2">
                {r.customer_name || '—'} · {(r.owner_name || '').trim() ? r.owner_name : (
                  <span className="inline-flex items-center gap-1 text-amber-800 font-semibold">
                    <Warning size={10} weight="bold" /> owner missing
                  </span>
                )}
              </p>
              <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#E2ECEA] text-center">
                <div>
                  <div className="text-[10px] text-[#6D8E87]">Amount</div>
                  <div className="text-xs font-bold text-[#3B5A54] tabular-nums">{fmtINR(r.amount)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#6D8E87]">Paid</div>
                  <div className="text-xs font-bold text-emerald-700 tabular-nums">{fmtINR(r.paid)}</div>
                </div>
                <div>
                  <div className="text-[10px] text-[#6D8E87]">Balance</div>
                  <div className="text-sm font-bold text-rose-700 tabular-nums">{fmtINR(r.balance)}</div>
                </div>
              </div>
              <div className="text-[10px] text-[#6D8E87] mt-2">Due: {r.due_date ? new Date(r.due_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</div>
            </Link>
          ))}
        </div>
      )}

      {/* Top offenders by owner — quick manager glance */}
      {totalByOwner.length > 0 && (
        <div className="medical-card p-4" data-testid="collections-top-owners">
          <h3 className="text-sm font-semibold text-[#3B5A54] mb-3">Outstanding by owner (top 5, filtered)</h3>
          <div className="space-y-2">
            {totalByOwner.map(([owner, amt]) => (
              <div key={owner} className="flex items-center justify-between text-sm">
                <span className="text-[#3B5A54]">{owner}</span>
                <span className="font-semibold text-[#00594F] tabular-nums">{fmtINR(amt)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// iter132.8 — Inline picker popover for reassigning ownerless leads without
// leaving the Collections page. Grouped by region; searchable when the org
// has more than 10 SEs.
function ReassignPopover({ users, busy, onSelect, onClose }) {
  const [q, setQ] = useState('');
  const filtered = users.filter((u) => {
    if (!q.trim()) return true;
    const hay = `${u.name || ''} ${u.email || ''} ${u.region || ''}`.toLowerCase();
    return hay.includes(q.trim().toLowerCase());
  });
  // Group by region for scan-ability (mirrors the Team page pattern).
  const byRegion = filtered.reduce((acc, u) => {
    const key = u.region || 'Unassigned';
    (acc[key] = acc[key] || []).push(u);
    return acc;
  }, {});
  const regions = Object.keys(byRegion).sort();
  return (
    <div
      className="absolute z-30 top-full left-0 mt-1 w-72 bg-white border border-[#D5E8E4] rounded-lg shadow-xl overflow-hidden"
      data-testid="reassign-popover"
      onMouseLeave={onClose}
    >
      <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-[#EAF3F0] bg-[#F4F9F7]">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-[#3B5A54]">Reassign to…</span>
        <button
          type="button"
          onClick={onClose}
          className="text-[#6D8E87] hover:text-[#052A23]"
          aria-label="Close"
          data-testid="reassign-popover-close"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
      {users.length > 10 && (
        <div className="px-2 py-2 border-b border-[#EAF3F0]">
          <input
            type="text"
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, role or region…"
            className="w-full px-2 py-1 text-sm border border-[#D5E8E4] rounded focus:ring-2 focus:ring-[#00bfb3] outline-none"
            data-testid="reassign-popover-search"
          />
        </div>
      )}
      <div className="max-h-64 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="px-3 py-6 text-center text-xs text-[#6D8E87]">No matches.</div>
        ) : (
          regions.map((region) => (
            <div key={region}>
              <div className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#6D8E87] bg-[#FAFDFC] sticky top-0">
                {region}
              </div>
              {byRegion[region].map((u) => (
                <button
                  key={u.id}
                  type="button"
                  disabled={busy}
                  onClick={() => onSelect(u.id)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-[#052A23] hover:bg-[#F4F9F7] disabled:opacity-50"
                  data-testid={`reassign-user-${u.id}`}
                >
                  <span className="truncate">{u.name}</span>
                  <span className="text-[10px] text-[#6D8E87] whitespace-nowrap">{u.role}</span>
                </button>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, border, testId }) {
  return (
    <div className={`medical-card p-4 border-l-4 ${border}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wide text-[#6D8E87]">{label}</span>
        {icon}
      </div>
      <div className="text-xl font-bold text-[#00594F] mt-2 tabular-nums" style={{ fontFamily: 'Outfit' }}>{value}</div>
      <div className="text-[11px] text-[#6D8E87] mt-1">{sub}</div>
    </div>
  );
}
